import * as React from 'react'
import { memo, useCallback } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { SxProps, Theme, useTheme } from '@mui/material'
import { useLocalStorage } from 'usehooks-ts'
import PanelLeftContractIcon from '@/components/Icons/PanelLeftContractIcon'
import PanelLeftExpandIcon from '@/components/Icons/PanelLeftExpandIcon'
import { getAppHeights } from '@/styles/utils'
import { useSizes } from '@/hooks/useSizes'
import { LocalStorageKey } from '@/types'
import Layout from '../Layout'
import Menu, { MenuProps } from '../Menu'

export interface MyPagesLayoutProps {
  menuItems: MenuProps['menuItems']
  onClickItem: MenuProps['onClickItem']
  children: React.ReactNode
  menuSX?: SxProps<Theme>
  skipBottomBar?: boolean
  customOnClickItemOnly?: boolean
  useLinks?: string
  testid?: string
  collapsible?: boolean
}

// Memoized Menu component to prevent re-renders
const MemoizedMenu = memo(Menu)

const MyPagesLayout = ({
  menuItems,
  onClickItem,
  children,
  menuSX,
  skipBottomBar,
  customOnClickItemOnly,
  useLinks,
  testid,
  collapsible = false,
}: MyPagesLayoutProps) => {
  const theme = useTheme()
  const appHeights = getAppHeights()
  const { isGreaterLg } = useSizes()
  // State for collapsed sidebar with localStorage persistence
  const [storedIsCollapsed, setStoredIsCollapsed] = useLocalStorage<boolean>(
    LocalStorageKey.SIDEBAR_COLLAPSED,
    false
  )

  // Determine collapsed state based on screen size and collapsible prop
  // On small screens (when isGreaterLg is false), always collapsed
  // On large screens, use stored value if collapsible is enabled
  let isCollapsed = false
  if (!isGreaterLg) {
    isCollapsed = true
  } else if (collapsible) {
    isCollapsed = storedIsCollapsed
  }

  // Toggle collapse handler - only works on large screens
  const handleToggleCollapse = useCallback(() => {
    if (collapsible && isGreaterLg) {
      setStoredIsCollapsed((prev: boolean) => !prev)
    }
  }, [collapsible, isGreaterLg, setStoredIsCollapsed])

  // Memoize the grid structure to prevent re-renders
  const gridStructure = React.useMemo(
    () => (
      <Box
        sx={{
          [theme.breakpoints.up('md')]: {
            display: 'flex',
            gap: 5,
            height: `calc(${appHeights.contentHeight})`,
            overflow: 'hidden',
          },
        }}
        className="My-Pages-Layout"
      >
        <Box
          data-testid="sidebar-container"
          sx={{
            overflowX: 'scroll',
            '&::-webkit-scrollbar': { display: 'none' },
            [theme.breakpoints.up('md')]: {
              minWidth: isCollapsed ? '88px' : '280px',
              maxWidth: isCollapsed ? '88px' : '280px',
              backgroundColor: 'common.white',
              py: 3.5,
              px: 2,
              paddingBottom: 2.5,
              transition: theme.transitions.create(
                ['min-width', 'max-width', 'padding'],
                {
                  duration: theme.transitions.duration.standard,
                  easing: theme.transitions.easing.easeInOut,
                }
              ),
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            },
            ...menuSX,
          }}
        >
          <MemoizedMenu
            menuItems={menuItems}
            onClickItem={onClickItem}
            customOnClickItemOnly={customOnClickItemOnly}
            useLinks={useLinks}
            testid={testid}
            isCollapsed={isCollapsed}
          />
          {collapsible && isGreaterLg && (
            <Button
              variant="text"
              fullWidth
              onClick={handleToggleCollapse}
              data-testid="sidebar-collapse-button"
              aria-label={isCollapsed ? 'Expand menu' : 'Collapse menu'}
              sx={{
                width: '100%',
                minWidth: '56px',
                height: '56px',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                gap: isCollapsed ? 0 : 1.5,
                color: 'text.secondary',
                padding: theme.spacing(1.5, isCollapsed ? 1 : 2),
                transition: theme.transitions.create(
                  ['gap', 'padding', 'justify-content'],
                  {
                    duration: theme.transitions.duration.standard,
                    easing: theme.transitions.easing.easeInOut,
                  }
                ),
                '&:active': {
                  backgroundColor: 'transparent',
                },
                '&:focus': {
                  backgroundColor: 'transparent',
                },
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              {isCollapsed ? (
                <PanelLeftExpandIcon sx={{ width: 20, height: 20 }} />
              ) : (
                <>
                  <PanelLeftContractIcon sx={{ width: 20, height: 20 }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  >
                    Collapse Menu
                  </Typography>
                </>
              )}
            </Button>
          )}
        </Box>
        <Box
          sx={{
            width: '100%',
            pt: { md: 5 },
            overflow: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {children}
        </Box>
      </Box>
    ),
    [
      theme,
      appHeights.contentHeight,
      isCollapsed,
      menuSX,
      menuItems,
      onClickItem,
      customOnClickItemOnly,
      useLinks,
      testid,
      collapsible,
      isGreaterLg,
      children,
      handleToggleCollapse,
    ]
  )

  return (
    <Layout skipBottomBar={skipBottomBar} withSideBar>
      {gridStructure}
    </Layout>
  )
}

// Create a named memoized component
const MemoizedMyPagesLayout = memo(MyPagesLayout)
export default MemoizedMyPagesLayout
